import {
  LayoutDashboard,
  Calendar,
  Settings,
  Users,
  UserCheck,
  Network,
  HelpCircle,
  MessageCircleQuestion,
  LogOut,
  User,
  Shield,
  ChevronsLeft,
  ClipboardCheck,
} from 'lucide-react';
import { useAuth } from '../../auth';
import { useNavigation } from '../../context/NavigationContext';
import { usePendingApprovalsCount } from '../../hooks/usePendingApprovalsCount';
import type { TabType } from './Navigation';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarSeparator,
  useSidebar,
} from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface NavGroup {
  label?: string;
  dotColor?: string;
  items: NavItem[];
  show?: 'always' | 'manager' | 'hr';
}

interface NavItem {
  id: TabType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const NAV_GROUPS: NavGroup[] = [
  {
    items: [
      { id: 'employee', label: 'Meine Übersicht', icon: LayoutDashboard },
    ],
  },
  {
    label: 'Arbeitszeit',
    dotColor: 'bg-blue-500',
    items: [
      { id: 'calendar', label: 'Kalender', icon: Calendar },
    ],
  },
  {
    label: 'Einstellungen',
    items: [
      { id: 'settings', label: 'Betriebsdaten', icon: Settings },
    ],
  },
  {
    label: 'Team',
    dotColor: 'bg-indigo-500',
    show: 'manager',
    items: [
      { id: 'manager', label: 'Team-Übersicht', icon: Users },
      { id: 'approvals', label: 'Genehmigungen', icon: ClipboardCheck },
    ],
  },
  {
    label: 'Personal',
    dotColor: 'bg-emerald-500',
    show: 'hr',
    items: [
      { id: 'hr', label: 'Mitarbeiter', icon: UserCheck },
      { id: 'orgchart', label: 'Organigramm', icon: Network },
      { id: 'hr-settings', label: 'Einstellungen', icon: Settings },
    ],
  },
  {
    label: 'Hilfe',
    items: [
      { id: 'help', label: 'Hilfe', icon: HelpCircle },
      { id: 'faq', label: 'FAQ', icon: MessageCircleQuestion },
    ],
  },
];

const EMPLOYEE_NAV_GROUPS: NavGroup[] = [
  {
    items: [
      { id: 'employee', label: 'Meine Übersicht', icon: LayoutDashboard },
    ],
  },
  {
    label: 'Hilfe',
    items: [
      { id: 'help', label: 'Hilfe', icon: HelpCircle },
      { id: 'faq', label: 'FAQ', icon: MessageCircleQuestion },
    ],
  },
];

export function AppSidebar() {
  const { activeTab, setActiveTab } = useNavigation();
  const { user, logout, isHR, isManager } = useAuth();
  const { toggleSidebar } = useSidebar();
  const pendingCount = usePendingApprovalsCount();

  const isEmployee = !isHR && !isManager;
  const groups = isEmployee ? EMPLOYEE_NAV_GROUPS : NAV_GROUPS;
  const visibleGroups = groups.filter((group) => {
    if (group.show === 'manager') return isManager;
    if (group.show === 'hr') return isHR;
    return true;
  });

  return (
    <Sidebar className="no-print border-r border-sidebar-border">
      <SidebarContent className="pt-2">
        {visibleGroups.map((group, groupIndex) => (
          <SidebarGroup key={groupIndex}>
            {group.label && (
              <SidebarGroupLabel>
                {group.dotColor && (
                  <span className={cn('w-2 h-2 rounded-full mr-2 shrink-0', group.dotColor)} />
                )}
                {group.label}
              </SidebarGroupLabel>
            )}
            <SidebarMenu>
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <SidebarMenuItem key={item.id}>
                    <SidebarMenuButton
                      isActive={activeTab === item.id}
                      onClick={() => setActiveTab(item.id)}
                      tooltip={item.label}
                    >
                      <Icon />
                      <span>{item.label}</span>
                      {item.id === 'approvals' && pendingCount > 0 && (
                        <span className="ml-auto min-w-[1.25rem] h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-5 text-center">
                          {pendingCount > 9 ? '9+' : pendingCount}
                        </span>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        <SidebarSeparator />
        {user && (
          <div className="flex items-center gap-3 px-2 py-1.5">
            <div className="p-1.5 bg-sidebar-accent rounded-full shrink-0">
              <User className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0 text-sm">
              <div className="font-medium truncate flex items-center gap-1">
                {user.name}
                {isHR && <Shield className="w-3.5 h-3.5 text-amber-500" aria-label="HR-Zugang" />}
              </div>
              <div className="text-xs text-sidebar-foreground/60 truncate">{user.email}</div>
            </div>
            <Button
              onClick={logout}
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              aria-label="Abmelden"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        )}
        <Button
          onClick={toggleSidebar}
          variant="ghost"
          size="sm"
          className="w-full justify-start gap-2 text-sidebar-foreground/60 hover:text-sidebar-foreground"
        >
          <ChevronsLeft className="w-4 h-4" />
          <span>Einklappen</span>
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
}
